/**
 * Cookie & privacy consent — ePrivacy / GDPR baseline.
 *
 * Essential storage (auth session, theme) is always allowed when the browser
 * permits it. Optional storage (diagnostics, offline cache, activation
 * funnel) requires explicit opt-in.
 */

import { storage } from "@/features/app-context/utils";
import { isLocalStorageAvailable } from "@/lib/clientStorage";

const CONSENT_KEY = "or:privacy-consent";
const CONSENT_VERSION = 2;
const LEGACY_CONSENT_VERSION = 1;

export type ConsentChoice = "essential" | "analytics";

interface ConsentRecord {
  version: typeof CONSENT_VERSION;
  choice: ConsentChoice;
  at: string;
}

function parseRecord(raw: string | null): ConsentRecord | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as {
      version?: number;
      choice?: ConsentChoice;
      at?: string;
    };
    if (
      parsed.version === LEGACY_CONSENT_VERSION &&
      (parsed.choice === "essential" || parsed.choice === "analytics") &&
      parsed.at
    ) {
      return {
        version: CONSENT_VERSION,
        choice: parsed.choice,
        at: parsed.at,
      };
    }
    if (parsed.version !== CONSENT_VERSION || !parsed.choice || !parsed.at) return null;
    return {
      version: CONSENT_VERSION,
      choice: parsed.choice,
      at: parsed.at,
    };
  } catch {
    return null;
  }
}

/** True when we can persist the user's consent choice locally. */
export function canPersistConsent(): boolean {
  return isLocalStorageAvailable();
}

/** Returns stored consent, or null if the user has not chosen yet. */
export function getConsent(): ConsentRecord | null {
  return parseRecord(storage.get(CONSENT_KEY));
}

/** Explicit opt-in to diagnostics, offline cache, and activation funnel. */
export function hasAnalyticsConsent(): boolean {
  return getConsent()?.choice === "analytics";
}

/** Alias — optional storage bundle (diagnostics + IndexedDB + funnel). */
export function hasOptionalStorageConsent(): boolean {
  return hasAnalyticsConsent();
}

export function hasConsentChoice(): boolean {
  return getConsent() !== null;
}

/** Persist consent when storage is available; returns whether the write succeeded. */
export function setConsent(choice: ConsentChoice): boolean {
  if (!canPersistConsent()) return false;
  storage.set(
    CONSENT_KEY,
    JSON.stringify({
      version: CONSENT_VERSION,
      choice,
      at: new Date().toISOString(),
    } satisfies ConsentRecord),
  );
  return true;
}

export function resetConsent(): void {
  storage.remove(CONSENT_KEY);
}
